/**
 * Contrato de borda do financeiro (contas a pagar, contas a receber e saldo inicial), camelCase,
 * exatamente como `supabase/functions/finance-runtime` responde. A validação recusa o que a tela
 * não sabe mostrar (estado desconhecido, valor não inteiro) em vez de exibir lixo.
 */
import type { PayableStatus, ReceivableStatus } from "../../../../packages/financial/src/cashflow.js";

export type { PayableStatus, ReceivableStatus };

export interface Payable {
  id: string;
  description: string;
  supplier: string | null;
  category: string | null;
  amountCents: number;
  dueDate: string;
  status: PayableStatus;
  paidAt: string | null;
  paidAmountCents: number | null;
  paymentMethod: string | null;
  installmentNo: number;
  installmentsTotal: number;
  groupId: string;
  notes: string | null;
}

export interface Receivable {
  id: string;
  platformOrderId: string | null;
  source: "bling_order" | "televendas" | "manual";
  orderRef: string | null;
  customerName: string | null;
  channelKind: string | null;
  description: string | null;
  installmentNo: number;
  installmentsTotal: number;
  amountCents: number;
  dueDate: string;
  status: ReceivableStatus;
  receivedAt: string | null;
  receivedAmountCents: number | null;
  paymentMethod: string | null;
  /** Baixa presumida na importação (o Bling não informa a baixa de cada parcela). */
  presumed: boolean;
  groupId: string;
  notes: string | null;
}

export interface FinanceSettings {
  openingBalanceCents: number;
  openingDate: string;
}

export interface NewPayableInput {
  description: string;
  supplier?: string;
  category?: string;
  amountCents: number;
  firstDueDate: string;
  installments?: number;
  intervalMonths?: number;
  paymentMethod?: string;
  notes?: string;
}

export interface NewReceivableInput {
  customerName: string;
  description?: string;
  orderRef?: string;
  channelKind?: string;
  amountCents: number;
  firstDueDate: string;
  installments?: number;
  intervalMonths?: number;
  paymentMethod?: string;
  source?: "televendas" | "manual";
  notes?: string;
}

export type SettleInput = { action: "pay" | "receive"; at?: string; amountCents?: number; paymentMethod?: string };
export type FinanceAction = { action: "cancel" | "reopen" };

function record(value: unknown, what: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(`${what}: payload is not an object`);
  return value as Record<string, unknown>;
}

export function assertPayable(value: unknown): Payable {
  const row = record(value, "Payable");
  if (typeof row.id !== "string" || !row.id) throw new Error("Payable: missing id");
  if (!Number.isInteger(row.amountCents)) throw new Error("Payable: amountCents must be integer cents");
  if (typeof row.dueDate !== "string") throw new Error("Payable: missing dueDate");
  if (row.status !== "open" && row.status !== "paid" && row.status !== "cancelled") throw new Error(`Payable: unknown status ${String(row.status)}`);
  return row as unknown as Payable;
}

export function assertReceivable(value: unknown): Receivable {
  const row = record(value, "Receivable");
  if (typeof row.id !== "string" || !row.id) throw new Error("Receivable: missing id");
  if (!Number.isInteger(row.amountCents)) throw new Error("Receivable: amountCents must be integer cents");
  if (typeof row.dueDate !== "string") throw new Error("Receivable: missing dueDate");
  if (row.status !== "open" && row.status !== "received" && row.status !== "cancelled") throw new Error(`Receivable: unknown status ${String(row.status)}`);
  return row as unknown as Receivable;
}

export function assertFinanceSettings(value: unknown): FinanceSettings {
  const row = record(value, "FinanceSettings");
  if (!Number.isInteger(row.openingBalanceCents)) throw new Error("FinanceSettings: openingBalanceCents must be integer cents");
  if (typeof row.openingDate !== "string") throw new Error("FinanceSettings: missing openingDate");
  return row as unknown as FinanceSettings;
}
